import { Modal } from '@base-app';
import { AddCampaignForm } from '../AddCampaignForm';
import { AddCampaignFormStringValues } from '../AddCampaignForm/AddCampaignForm.types';
import { Sizes } from '@/common/types';
import { useTranslation } from 'react-i18next';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { transformICampaignToAddCampaignFormStringValues, validateFormFn } from './EditCampaignModal.helper';
import { ICampaign } from '@/modules/study-insights/services/campaign';
import { useMemo } from 'react';
import { useEditCampaign } from './hooks/useEditCampaign';
import { parse } from 'date-fns';

interface EditCampaignModalProps {
  show: boolean;
  onClose: () => void;
  onEditCampaign: (values: AddCampaignFormStringValues | undefined) => void;
  onErrorEditCampaign?: (error: any) => void;
  modalTitle: string;
  values?: ICampaign;
}

export const EditCampaignModal = ({
  show,
  onClose,
  // To be used in integration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onEditCampaign,
  // To be used in integration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onErrorEditCampaign,
  modalTitle,
  values,
}: EditCampaignModalProps): JSX.Element => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const valuesAsString = useMemo(
    () => (values ? transformICampaignToAddCampaignFormStringValues(values) : undefined),
    [values],
  );
  const {
    variables: campaignValues,
    isLoading: isCampaignEditLoading,
    mutateAsync: editCampaign,
  } = useEditCampaign({
    onSuccess: () => onEditCampaign(campaignValues),
    onError: onErrorEditCampaign,
  });

  const handleOnSubmit = async (values: AddCampaignFormStringValues): Promise<void> => {
    const formatDate = (date: string): string => {
      // If the date is already in the correct format
      // Unfortunately the date is type string and the backend return in ISO format
      // and the input date is in MM/dd/yyyy format
      if (date.includes('T')) {
        return date;
      }
      // This is the data that the input date delivers and needs to be parsed
      // to the api format or ISO format
      return parse(date, 'MM/dd/yyyy', new Date()).toISOString();
    };

    const validDistributionValues = values.volumeDistribution?.map((distribution) => ({
      numberOfEnrollees: distribution.numberOfEnrollees,
      distributionDate: formatDate(distribution.distributionDate ?? ''),
    }));

    const payload: AddCampaignFormStringValues = {
      // At this point all validations should take place and no
      // empty values should exists on mandatory values
      campaignName: values.campaignName ?? '',
      campaignSource: values.campaignSource ?? '',
      // This value is not edited at the moment
      campaignMedium: values.campaignMedium ?? '',
      campaignUrl: undefined,
      launchDate: values.launchDate ? formatDate(values.launchDate) : formatDate(new Date().toISOString()),
      endDate: values.endDate ? formatDate(values.endDate) : undefined,
      campaignTerm: values.campaignTerm,
      campaignContent: values.campaignContent,
      volumeDistribution: validDistributionValues ? validDistributionValues ?? undefined : undefined,
      campaignID: values.campaignID,
    };
    await editCampaign(payload);
  };

  return (
    <Modal show={show} title={modalTitle} onClose={onClose} size={Sizes.XXXX_LARGE} isLocked={false}>
      <AddCampaignForm
        t={t}
        isLoading={isCampaignEditLoading}
        onCancel={onClose}
        onSubmit={handleOnSubmit}
        showItems={['dates', 'volumeDistribution']}
        validateFormFn={validateFormFn}
        values={valuesAsString}
        mainButtonLabel={t('add_campaign.options.edit')}
      />
    </Modal>
  );
};
